import React, { useState, useMemo } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import * as Icons from "lucide-react";
import { SectionHeading } from "../components/SectionHeading";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Slider } from "../components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { FRANCHISE_PLANS, FRANCHISE_SEGMENTS } from "../lib/data";
import { submitFranchise } from "../lib/api";

const money = (n) => "₹" + Math.round(n).toLocaleString("en-IN");

export default function Franchise() {
  const [invest, setInvest] = useState(500000);
  const [roi, setRoi] = useState(28);
  const [months, setMonths] = useState(12);
  const [form, setForm] = useState({ name: "", phone: "", email: "", city: "", segment: "", plan: "", investment: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const choosePlan = (name) => {
    setForm((f) => ({ ...f, plan: name }));
    toast.success(`${name} plan selected — fill the form to apply`);
    setTimeout(() => document.getElementById("franchise-apply")?.scrollIntoView({ behavior: "smooth", block: "center" }), 100);
  };

  const projected = useMemo(() => {
    const annualReturn = invest * (roi / 100);
    const totalReturn = annualReturn * (months / 12);
    return { annualReturn, totalReturn, totalValue: invest + totalReturn };
  }, [invest, roi, months]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.segment) { toast.error("Please fill name, phone and segment"); return; }
    setLoading(true);
    try { await submitFranchise({ ...form, investment: form.investment || String(invest) }); toast.success("Application submitted! Our franchise team will call you."); setForm({ name: "", phone: "", email: "", city: "", segment: "", plan: "", investment: "", message: "" }); }
    catch { toast.error("Could not submit. Try WhatsApp or call."); } finally { setLoading(false); }
  };

  return (
    <div data-testid="franchise-page" className="pt-28 pb-20 bg-cream-100 text-soil-700 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <div className="inline-block text-xs uppercase tracking-[0.2em] font-bold text-terracotta-600 mb-3">Franchise & Partnership</div>
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold text-forest-800">Own a Piece of the <span className="saffron-text">Genesis Empire</span></h1>
          <p className="text-soil-500 mt-4">Partner with a 30-year multi-sector group. Low-risk models, strong ROI, and full training & marketing support.</p>
        </div>

        {/* PLANS */}
        <div className="grid md:grid-cols-3 gap-6 mb-16">
          {FRANCHISE_PLANS.map((p) => (
            <motion.div key={p.name} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              className={`card-cream rounded-2xl p-7 relative transition-all ${p.popular ? "border-forest-500/50" : ""} ${form.plan === p.name ? "ring-2 ring-forest-600 border-forest-600" : ""}`} data-testid={`plan-${p.name.toLowerCase()}`}>
              {p.popular && <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] font-bold px-3 py-1 rounded-full forest-gradient text-cream-50">MOST POPULAR</span>}
              <h3 className="font-heading text-xl font-bold text-forest-800">{p.name}</h3>
              <div className="font-mono-fin text-2xl font-extrabold saffron-text mt-2">{p.investment}</div>
              <div className="flex gap-4 mt-3 text-sm"><span className="text-approval font-semibold">{p.roi} ROI</span><span className="text-soil-500">Break-even {p.period}</span></div>
              <ul className="mt-5 space-y-2">{p.perks.map((k) => (<li key={k} className="flex items-start gap-2 text-sm text-soil-600"><Icons.Check className="w-4 h-4 text-terracotta-600 shrink-0 mt-0.5" />{k}</li>))}</ul>
              <Button onClick={() => choosePlan(p.name)} data-testid={`plan-choose-${p.name.toLowerCase()}`} className={`w-full mt-6 font-bold h-11 rounded-full ${form.plan === p.name ? "bg-approval text-forest-800" : "forest-gradient text-cream-50"}`}>{form.plan === p.name ? "✓ Selected" : `Choose ${p.name}`}</Button>
            </motion.div>
          ))}
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* ROI CALCULATOR */}
          <div className="card-cream rounded-2xl p-6 sm:p-8" data-testid="roi-calculator">
            <h2 className="font-heading text-2xl font-bold text-forest-800 mb-6 flex items-center gap-2"><Icons.TrendingUp className="w-5 h-5 text-terracotta-600" /> ROI Calculator</h2>
            <div className="space-y-7">
              <div>
                <div className="flex justify-between mb-3"><Label className="text-soil-600">Investment</Label><span className="font-mono-fin text-terracotta-600 font-bold" data-testid="roi-invest-value">{money(invest)}</span></div>
                <Slider value={[invest]} min={200000} max={5000000} step={50000} onValueChange={(v) => setInvest(v[0])} data-testid="roi-invest-slider" />
              </div>
              <div>
                <div className="flex justify-between mb-3"><Label className="text-soil-600">Expected ROI (p.a.)</Label><span className="font-mono-fin text-terracotta-600 font-bold">{roi}%</span></div>
                <Slider value={[roi]} min={15} max={40} step={1} onValueChange={(v) => setRoi(v[0])} data-testid="roi-rate-slider" />
              </div>
              <div>
                <div className="flex justify-between mb-3"><Label className="text-soil-600">Duration (months)</Label><span className="font-mono-fin text-terracotta-600 font-bold">{months} mo</span></div>
                <Slider value={[months]} min={6} max={60} step={6} onValueChange={(v) => setMonths(v[0])} data-testid="roi-months-slider" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 mt-6">
              <div className="rounded-xl bg-white border border-cream-300 p-4 text-center"><div className="text-xs text-soil-500">Projected Returns</div><div className="font-mono-fin font-bold text-approval text-lg" data-testid="roi-returns">{money(projected.totalReturn)}</div></div>
              <div className="rounded-xl bg-saffron-100 border border-saffron-500/30 p-4 text-center"><div className="text-xs text-soil-600">Total Value</div><div className="font-mono-fin font-bold text-terracotta-600 text-lg" data-testid="roi-total">{money(projected.totalValue)}</div></div>
            </div>
            <p className="text-[11px] text-soil-400 mt-3">*Indicative projection only. Actual returns vary by segment, location & effort.</p>
          </div>

          {/* APPLY FORM */}
          <div className="card-cream rounded-2xl p-6 sm:p-8" id="franchise-apply">
            <h2 className="font-heading text-2xl font-bold text-forest-800 mb-6">Apply for Franchise</h2>
            <form onSubmit={submit} className="space-y-3.5" data-testid="franchise-form">
              <div className="grid sm:grid-cols-2 gap-3.5">
                <Input data-testid="fr-name" placeholder="Full Name *" value={form.name} onChange={(e) => set("name", e.target.value)} className="bg-white border-cream-300 h-11 text-forest-800" />
                <Input data-testid="fr-phone" placeholder="Phone *" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="bg-white border-cream-300 h-11 text-forest-800" />
              </div>
              <div className="grid sm:grid-cols-2 gap-3.5">
                <Input data-testid="fr-email" placeholder="Email" value={form.email} onChange={(e) => set("email", e.target.value)} className="bg-white border-cream-300 h-11 text-forest-800" />
                <Input data-testid="fr-city" placeholder="City" value={form.city} onChange={(e) => set("city", e.target.value)} className="bg-white border-cream-300 h-11 text-forest-800" />
              </div>
              <Select value={form.segment} onValueChange={(v) => set("segment", v)}>
                <SelectTrigger data-testid="fr-segment" className="bg-white border-cream-300 h-11 text-forest-800"><SelectValue placeholder="Franchise Segment *" /></SelectTrigger>
                <SelectContent className="bg-cream-50 border-cream-300 text-forest-800">{FRANCHISE_SEGMENTS.map((s) => (<SelectItem key={s} value={s} className="focus:bg-forest-100 focus:text-forest-800">{s}</SelectItem>))}</SelectContent>
              </Select>
              <Input data-testid="fr-investment" placeholder={`Investment budget (${form.plan || "e.g. ₹5 Lakh"})`} value={form.investment} onChange={(e) => set("investment", e.target.value)} className="bg-white border-cream-300 h-11 text-forest-800" />
              <Textarea data-testid="fr-message" placeholder="Tell us about yourself / your city..." value={form.message} onChange={(e) => set("message", e.target.value)} className="bg-white border-cream-300 text-forest-800" />
              <Button type="submit" disabled={loading} data-testid="fr-submit" className="w-full forest-gradient text-cream-50 font-bold h-12 rounded-full">{loading ? <Icons.Loader2 className="w-5 h-5 animate-spin" /> : "Apply for Franchise"}</Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
